import moment from "moment";

export const MONTH_LABELS = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

export const toNum = (v) => {
  if (v === null || v === undefined || isNaN(Number(v))) return 0;
  return Number(v);
};

export const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  return moment(dateStr).format("DD-MM-YYYY");
};

/**
 * Normalize API response rows into uniform objects with display labels and numbers.
 *
 * @param {string} indicator - "indikator_31" or "indikator_32"
 * @param {Array} data - raw data from API
 * @returns {Array} normalized rows
 */
export const buildTrendRows = (indicator, data = []) => {
  const list = Array.isArray(data) ? data : [];

  if (indicator === "indikator_31") {
    return list.map((r) => {
      const b = toNum(r?.bulan);
      const label = MONTH_LABELS[b - 1] || String(r?.bulan ?? "");
      return {
        bulan: b,
        label,
        jumlah_tp_disurvei: toNum(r?.jumlah_tp_disurvei),
        tp_masih_ada: toNum(r?.tp_masih_ada),
        tp_positif: toNum(r?.tp_positif),
        ih_percent: toNum(r?.ih_percent),
      };
    });
  }

  if (indicator === "indikator_32") {
    return list.map((r) => ({
      tanggal: r?.tanggal || "",
      label: formatDateDDMMYYYY(r?.tanggal),
      total_kunjungan: toNum(r?.total_kunjungan),
      tp_masih_ada: toNum(r?.tp_masih_ada),
      tp_positif: toNum(r?.tp_positif),
      tp_diintervensi: toNum(r?.tp_diintervensi),
      ih_percent: toNum(r?.ih_percent),
    }));
  }

  return [];
};

/**
 * Calculate total row with sums of count columns and overall IH (%).
 *
 * @param {string} indicator
 * @param {Array} data
 * @returns {Object} total row
 */
export const sumTrendRows = (indicator, data = []) => {
  const rows = buildTrendRows(indicator, data);

  if (indicator === "indikator_31") {
    let sumJumlah = 0;
    let sumMasihAda = 0;
    let sumPositif = 0;

    rows.forEach((r) => {
      sumJumlah += r.jumlah_tp_disurvei;
      sumMasihAda += r.tp_masih_ada;
      sumPositif += r.tp_positif;
    });

    const ihPercent =
      sumMasihAda === 0
        ? "0.0%"
        : `${((sumPositif * 100) / sumMasihAda).toFixed(1)}%`;

    return {
      jumlah_tp_disurvei: sumJumlah,
      tp_masih_ada: sumMasihAda,
      tp_positif: sumPositif,
      ih_percent: ihPercent,
    };
  }

  if (indicator === "indikator_32") {
    let sumTotalKunjungan = 0;
    let sumMasihAda = 0;
    let sumPositif = 0;
    let sumDiintervensi = 0;

    rows.forEach((r) => {
      sumTotalKunjungan += r.total_kunjungan;
      sumMasihAda += r.tp_masih_ada;
      sumPositif += r.tp_positif;
      sumDiintervensi += r.tp_diintervensi;
    });

    const ihPercent =
      sumMasihAda === 0
        ? "0.0%"
        : `${((sumPositif * 100) / sumMasihAda).toFixed(1)}%`;

    return {
      total_kunjungan: sumTotalKunjungan,
      tp_masih_ada: sumMasihAda,
      tp_positif: sumPositif,
      tp_diintervensi: sumDiintervensi,
      ih_percent: ihPercent,
    };
  }

  return {};
};

const toChartPoint = (val) => {
  const pt = { value: val };
  if (val === 0) {
    pt.showValue = "0";
  }
  return pt;
};

const toIhChartPoint = (ihVal, tpMasihAda) => {
  const pt = { value: ihVal };
  if (ihVal === 0 || tpMasihAda === 0) {
    pt.showValue = "0";
  }
  return pt;
};

/**
 * Build FusionCharts dataSource for mscombidy2d.
 *
 * @param {string} indicator
 * @param {Array} data
 * @param {Object} options
 * @returns {Object} FusionCharts dataSource
 */
export const buildChartDataSource = (
  indicator,
  data = [],
  { indicatorName = "", levelIdSelected = "" } = {}
) => {
  const rows = buildTrendRows(indicator, data);
  const categories = [{ category: rows.map((r) => ({ label: r.label })) }];

  const exportFileName = `${(indicatorName || "").replace(
    / /g,
    "_"
  )}_${String(levelIdSelected || "").replace(/ /g, "_")}_${moment().format(
    "DDMMYYYY"
  )}`;

  let maxCount = 0;
  if (indicator === "indikator_31") {
    rows.forEach((r) => {
      maxCount = Math.max(
        maxCount,
        r.jumlah_tp_disurvei,
        r.tp_masih_ada,
        r.tp_positif
      );
    });
  } else if (indicator === "indikator_32") {
    rows.forEach((r) => {
      maxCount = Math.max(
        maxCount,
        r.total_kunjungan,
        r.tp_masih_ada,
        r.tp_positif,
        r.tp_diintervensi
      );
    });
  }

  const pMax = Math.max(4, Math.ceil((maxCount * 1.1) / 4) * 4);

  const chart = {
    theme: "fusion",
    showValues: "1",
    pYAxisName: "Jumlah TP",
    pYAxisMinValue: "0",
    pYAxisMaxValue: String(pMax),
    sYAxisName: "IH (%)",
    sYAxisMinValue: "0",
    sYAxisMaxValue: "100",
    numDivLines: "3",
    adjustDiv: "0",
    sNumberSuffix: "%",
    exportEnabled: "1",
    exportFileName,
  };

  let dataset = [];

  if (indicator === "indikator_31") {
    dataset = [
      {
        seriesName: "Jumlah TP Disurvei",
        data: rows.map((r) => toChartPoint(r.jumlah_tp_disurvei)),
      },
      {
        seriesName: "TP Masih Ada",
        data: rows.map((r) => toChartPoint(r.tp_masih_ada)),
      },
      {
        seriesName: "TP Positif",
        data: rows.map((r) => toChartPoint(r.tp_positif)),
      },
      {
        seriesName: "IH (%)",
        parentYAxis: "S",
        renderAs: "line",
        data: rows.map((r) => toIhChartPoint(r.ih_percent, r.tp_masih_ada)),
      },
    ];
  } else if (indicator === "indikator_32") {
    dataset = [
      {
        seriesName: "Total Kunjungan",
        data: rows.map((r) => toChartPoint(r.total_kunjungan)),
      },
      {
        seriesName: "TP Masih Ada",
        data: rows.map((r) => toChartPoint(r.tp_masih_ada)),
      },
      {
        seriesName: "TP Positif",
        data: rows.map((r) => toChartPoint(r.tp_positif)),
      },
      {
        seriesName: "TP Diintervensi",
        data: rows.map((r) => toChartPoint(r.tp_diintervensi)),
      },
      {
        seriesName: "IH (%)",
        parentYAxis: "S",
        renderAs: "line",
        data: rows.map((r) => toIhChartPoint(r.ih_percent, r.tp_masih_ada)),
      },
    ];
  }

  return {
    chart,
    categories,
    dataset,
  };
};
